import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CategoryDTO, UnitOfMeasureDTO, SupplierDTO, EnhancedItemDTO } from '../inventory-dashboard.component';
import { SingleSelectDropdownComponent } from '../../business-details/single-select-dropdown.component';
import { AreaQuantityInputComponent } from '../../../shared/area-quantity-input/area-quantity-input.component';
import { TooltipComponent } from '../../../shared/tooltip/tooltip.component';
import { OffcanvasSheetComponent } from '../../shared/offcanvas-sheet/offcanvas-sheet.component';
import { NumericInputDirective } from '../../../shared/directives/numeric-input.directive';

export type InventoryOffcanvasMode = 'raw' | 'product';

export interface BOMComponentDraft {
  rawMaterialItemID: number | null;
  quantityRequired: number;
  componentUOMID: number | null;
}

@Component({
  selector: 'app-inventory-item-offcanvas',
  standalone: true,
  imports: [CommonModule, FormsModule, SingleSelectDropdownComponent, AreaQuantityInputComponent, TooltipComponent, OffcanvasSheetComponent, NumericInputDirective],
  templateUrl: './inventory-item-offcanvas.component.html',
  styleUrls: [
    '../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './inventory-item-offcanvas.component.css',
  ],
})
export class InventoryItemOffcanvasComponent implements OnChanges {
  @Input() visible = false;
  @Input() mode: InventoryOffcanvasMode = 'raw';
  @Input() item: EnhancedItemDTO | null = null;
  @Input() isEditing = false;
  @Input() isAddingQuantity = false;
  @Input() selectedItemForQtyAdd: EnhancedItemDTO | null = null;
  @Input() categories: CategoryDTO[] = [];
  @Input() unitsOfMeasure: UnitOfMeasureDTO[] = [];
  @Input() suppliers: SupplierDTO[] = [];
  @Input() rawMaterials: EnhancedItemDTO[] = [];

  @Output() closeCanvas = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Output() addNewCategory = new EventEmitter<void>();
  @Output() addNewSupplier = new EventEmitter<void>();

  readonly Math = Math;
  readonly String = String;

  toOption = (id: number | null | undefined, label: string) => ({ value: String(id ?? ''), label });
  fromValue = (v: string | null) => (v == null || v === '' ? null : Number(v));

  get categoryOptions() { return this.categories.map(c => this.toOption(c.categoryId, c.categoryName)); }
  get uomOptions() { return this.unitsOfMeasure.map(u => this.toOption(u.uomid, u.uomName)); }
  get supplierOptions() { return this.suppliers.map(s => this.toOption(s.supplierID, s.companyName)); }
  get rawMaterialOptions() { return this.rawMaterials.map(r => this.toOption(r.itemID, r.itemName)); }

  editData: any = {};
  bomComponents: BOMComponentDraft[] = [];
  bomTotalCost = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible && (changes['visible'] || changes['item'] || changes['selectedItemForQtyAdd'])) {
      if (this.isAddingQuantity && this.selectedItemForQtyAdd) {
        this.editData = {
          itemName: this.selectedItemForQtyAdd.itemName,
          quantity: this.selectedItemForQtyAdd.quantity ?? 0,
          quantityToAdd: 1,
        };
      } else if (this.item) {
        this.editData = {
          itemName: this.item.itemName,
          itemCode: this.item.itemCode,
          categoryID: this.item.categoryID,
          purchaseUOMID: this.item.purchaseUOMID,
          quantity: this.item.quantity ?? 0,
          purchasePrice: this.item.purchasePrice ?? 0,
          unitCost: this.item.unitCost ?? 0,
          purchaseQuantity: this.item.purchaseQuantity ?? 1,
          lowStockThreshold: this.item.lowStockThreshold ?? 0,
          sellingPrice: this.item.sellingPrice ?? 0,
          supplierID: this.item.supplierID ?? null,
          isActive: this.item.isActive ?? true,
        };
      } else {
        this.editData = {
          itemName: '',
          itemCode: '',
          categoryID: null,
          purchaseUOMID: null,
          quantity: 0,
          quantityToAdd: 0,
          purchasePrice: 0,
          unitCost: 0,
          purchaseQuantity: 1,
          lowStockThreshold: 0,
          sellingPrice: 0,
          supplierID: null,
          isActive: true,
        };
        this.bomComponents = [];
        this.bomTotalCost = 0;
      }
    }
  }

  get title(): string {
    if (this.isAddingQuantity) return 'Add Quantity';
    const verb = this.isEditing ? 'Edit' : 'Add';
    return this.mode === 'raw' ? `${verb} Raw Material` : `${verb} Product`;
  }

  getUomCode(uomId: number | null): string {
    if (!uomId) return '';
    return (this.unitsOfMeasure.find(u => u.uomid === uomId)?.uomCode ?? '').replace(/2$/, '²');
  }

  addBOMComponent(): void {
    this.bomComponents.push({ rawMaterialItemID: null, quantityRequired: 1, componentUOMID: null });
  }

  removeBOMComponent(index: number): void {
    this.bomComponents.splice(index, 1);
    this.recalcBOMCost();
  }

  onBOMRawMaterialChange(index: number): void {
    const comp = this.bomComponents[index];
    const rm = this.rawMaterials.find(r => r.itemID === Number(comp.rawMaterialItemID));
    if (rm) {
      comp.componentUOMID = rm.purchaseUOMID;
    }
    this.recalcBOMCost();
  }

  recalcBOMCost(): void {
    this.bomTotalCost = this.bomComponents.reduce((total, comp) => {
      const rm = this.rawMaterials.find(r => r.itemID === Number(comp.rawMaterialItemID));
      if (!rm) return total;
      const unitCost = rm.purchaseQuantity > 0 ? rm.purchasePrice / rm.purchaseQuantity : rm.purchasePrice;
      return total + (comp.quantityRequired * unitCost);
    }, 0);
  }

  purchasePriceError: string | null = null;

  private recalculateUnitCost(): void {
    if (this.mode !== 'raw' || this.isAddingQuantity) return;

    const rawPrice = this.editData?.purchasePrice;
    const price = Number(rawPrice);
    if (rawPrice !== null && rawPrice !== undefined && rawPrice !== '' && (isNaN(price) || price < 0)) {
      this.purchasePriceError = isNaN(price)
        ? `"${rawPrice}" is not a valid price.`
        : 'Purchase price cannot be negative.';
      return;
    }
    this.purchasePriceError = null;

    const validPrice = isNaN(price) ? 0 : price;
    const qty = this.isEditing
      ? (Number(this.editData?.purchaseQuantity) || 0)
      : (Number(this.editData?.quantityToAdd) || 0);
    this.editData.unitCost = qty > 0 ? parseFloat((validPrice / qty).toFixed(2)) : 0;
  }

  onPurchasePriceChange(): void { this.recalculateUnitCost(); }
  onInitialQuantityChange(): void { this.recalculateUnitCost(); }
  onPurchaseQtyChange(): void { this.recalculateUnitCost(); }

  onAddNewCategory(): void { this.addNewCategory.emit(); }
  onAddNewSupplier(): void { this.addNewSupplier.emit(); }

  onSave(): void {
    const base = { ...this.editData };
    let itemData: any;
    if (!this.isEditing && !this.isAddingQuantity && this.mode === 'raw') {
      itemData = { ...base, quantity: Number(base.quantityToAdd) || 0 };
    } else if (this.isEditing && this.mode === 'raw' && this.item) {
      const origPrice = this.item.purchasePrice ?? 0;
      const origQty = this.item.purchaseQuantity ?? 1;
      const newPrice = Number(base.purchasePrice) || 0;
      const newQty = Number(base.purchaseQuantity) || origQty;
      const hasPurchaseChange =
        Math.abs(newPrice - origPrice) > 0.01 ||
        (newQty !== origQty && newQty > 0);
      itemData = { ...base, purchaseQuantity: newQty, hasPurchaseChange };
    } else {
      itemData = base;
    }
    this.save.emit({
      itemData,
      bomComponents: this.mode === 'product' ? [...this.bomComponents] : [],
      isEditing: this.isEditing,
      isAddingQuantity: this.isAddingQuantity,
      mode: this.mode,
      itemID: this.item?.itemID ?? (this.selectedItemForQtyAdd?.itemID ?? null),
      selectedItem: this.isAddingQuantity ? this.selectedItemForQtyAdd : null,
    });
    this.onClose();
  }

  onClose(): void {
    this.closeCanvas.emit();
  }
}
